import React, { useId } from 'react';
import './CloudArtwork.css';
import { useSkin } from './SkinContext';
import SkinArtwork from './SkinArtwork';

/** A small, self-contained illustration; no image or font requests are needed. */
const CloudArtwork = ({ className = '', variant = 'default' }) => {
  const id = `cloud-art-${useId().replace(/:/g, '')}`;
  const skin = useSkin();
  const paint = (name) => `url(#${id}-${name})`;

  if (['orbital', 'terminal', 'atelier'].includes(skin)) {
    return <SkinArtwork skin={skin} className={`cloud-artwork cloud-artwork--${variant} ${className}`} />;
  }

  return (
    <div className={`cloud-artwork cloud-artwork--${variant} ${className}`} aria-hidden="true">
      <svg viewBox="0 0 500 350" fill="none" focusable="false">
        <defs>
          <radialGradient id={`${id}-glow`}>
            <stop stopColor="#fff2db" stopOpacity=".82" />
            <stop offset=".56" stopColor="#ffdecf" stopOpacity=".34" />
            <stop offset="1" stopColor="#ffdecf" stopOpacity="0" />
          </radialGradient>
          <linearGradient id={`${id}-sun`} x1="291" y1="56" x2="326" y2="137" gradientUnits="userSpaceOnUse">
            <stop stopColor="#fff5dd" />
            <stop offset="1" stopColor="#efc4b0" stopOpacity=".62" />
          </linearGradient>
          <linearGradient id={`${id}-blue`} x1="120" y1="124" x2="347" y2="233" gradientUnits="userSpaceOnUse">
            <stop stopColor="#f4f8ff" stopOpacity=".94" />
            <stop offset=".48" stopColor="#bfd3ed" stopOpacity=".84" />
            <stop offset="1" stopColor="#afbddc" stopOpacity=".6" />
          </linearGradient>
          <linearGradient id={`${id}-rose`} x1="294" y1="113" x2="367" y2="234" gradientUnits="userSpaceOnUse">
            <stop stopColor="#fff8f3" />
            <stop offset=".55" stopColor="#f7dce1" stopOpacity=".94" />
            <stop offset="1" stopColor="#cbbcd8" stopOpacity=".65" />
          </linearGradient>
          <linearGradient id={`${id}-body`} x1="208" y1="134" x2="251" y2="287" gradientUnits="userSpaceOnUse">
            <stop stopColor="#fffdf8" />
            <stop offset=".35" stopColor="#fffcfb" stopOpacity=".98" />
            <stop offset=".73" stopColor="#eef2fb" stopOpacity=".97" />
            <stop offset="1" stopColor="#d8e2f2" stopOpacity=".94" />
          </linearGradient>
          <radialGradient id={`${id}-blush`} cx="0" cy="0" r="1" gradientTransform="translate(318 232) rotate(169) scale(116 55)" gradientUnits="userSpaceOnUse">
            <stop stopColor="#f7dbdc" stopOpacity=".74" />
            <stop offset="1" stopColor="#f7dbdc" stopOpacity="0" />
          </radialGradient>
          <linearGradient id={`${id}-edge`} x1="170" y1="143" x2="238" y2="275" gradientUnits="userSpaceOnUse">
            <stop stopColor="white" />
            <stop offset=".48" stopColor="white" stopOpacity=".74" />
            <stop offset="1" stopColor="white" stopOpacity=".15" />
          </linearGradient>
          <linearGradient id={`${id}-wisp`} x1="76" y1="237" x2="169" y2="290" gradientUnits="userSpaceOnUse">
            <stop stopColor="#fffdfa" />
            <stop offset="1" stopColor="#e7e9f6" stopOpacity=".78" />
          </linearGradient>
          <radialGradient id={`${id}-shadow`}>
            <stop stopColor="#7180a5" stopOpacity=".13" />
            <stop offset="1" stopColor="#7180a5" stopOpacity="0" />
          </radialGradient>
          <filter id={`${id}-soft-shadow`} x="-20%" y="-30%" width="140%" height="170%" colorInterpolationFilters="sRGB">
            <feDropShadow dx="0" dy="11" stdDeviation="10" floodColor="#8292b6" floodOpacity=".13" />
          </filter>
          <path id={`${id}-silhouette`} d="M139 259c-31 1-50-16-48-38 1-22 20-38 43-37 5-31 26-51 53-50 26 0 48 17 53 40 13-16 34-23 54-18 20 4 34 20 37 37 27-4 50 12 52 35 3 26-18 43-46 42-17 15-51 17-75 8-23 12-55 10-73-1-18 4-40-2-50-18Z" />
        </defs>

        <circle cx="312" cy="110" r="106" fill={paint('glow')} />
        <circle cx="309" cy="103" r="40" fill={paint('sun')} stroke="#fff7e9" strokeOpacity=".8" />
        <ellipse cx="253" cy="302" rx="159" ry="22" fill={paint('shadow')} />

        <g className="cloud-artwork__back">
          <path d="M116 212c-24-1-41-15-40-34 1-19 18-33 38-32 3-27 24-44 49-41 20 2 36 16 40 35 22-10 48 4 49 26 24-3 41 10 41 28 0 20-18 29-43 28l-94 4c-16 1-31-5-40-14Z" fill={paint('blue')} stroke="#f8fbff" strokeOpacity=".65" strokeWidth="1.2" />
          <path d="M308 224c-25 1-44-13-44-32 0-21 17-36 38-36 3-24 21-40 44-38 19 2 33 14 37 30 21-5 40 8 42 27 17 3 28 15 26 29-2 17-18 25-40 23-10 7-28 9-42 4-22 7-45 4-61-7Z" fill={paint('rose')} stroke="#fff9f5" strokeOpacity=".7" strokeWidth="1.2" />
        </g>

        <g className="cloud-artwork__front" filter={paint('soft-shadow')}>
          <use href={`#${id}-silhouette`} fill={paint('body')} />
          <use href={`#${id}-silhouette`} fill={paint('blush')} />
          <use href={`#${id}-silhouette`} stroke={paint('edge')} strokeWidth="1.7" />
          <path d="M143 184c5-25 22-40 43-40 17 0 33 9 40 23M254 172c15-10 33-9 44-3" stroke="white" strokeOpacity=".86" strokeWidth="2.8" strokeLinecap="round" />
          <path d="M119 232c5 11 16 17 30 17m141 20c15 2 29-1 38-8" stroke="#c9d6eb" strokeOpacity=".46" strokeWidth="1.3" strokeLinecap="round" />
        </g>

        <g className="cloud-artwork__wisp">
          <path d="M71 278c-13 0-22-8-21-19 1-11 10-18 22-18 3-16 15-26 29-24 14 1 24 12 25 24 13-5 28 3 31 15 14 0 23 7 22 17-1 11-12 17-27 15-15 5-32 3-42-3-15 4-31 1-39-7Z" fill={paint('wisp')} stroke="white" strokeOpacity=".82" />
          <path d="M78 238c3-9 10-14 19-14" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </g>

        <g className="cloud-artwork__stars" strokeLinecap="round" strokeLinejoin="round">
          <path d="M247 69c1.5 8 5 11.5 13 13-8 1.5-11.5 5-13 13-1.5-8-5-11.5-13-13 8-1.5 11.5-5 13-13Z" fill="#fff8e9" fillOpacity=".8" stroke="#cfaa91" strokeWidth="1.1" />
          <path d="M409 258c1 5.5 3.5 8 9 9-5.5 1-8 3.5-9 9-1-5.5-3.5-8-9-9 5.5-1 8-3.5 9-9Z" fill="#fcf7f0" stroke="#9baecb" strokeWidth="1" />
          <circle cx="83" cy="120" r="2.5" fill="#f9e1cc" />
          <circle cx="387" cy="90" r="2" fill="#fffaf1" />
          <circle cx="209" cy="308" r="2" fill="#e3b8b3" fillOpacity=".7" />
        </g>
      </svg>
    </div>
  );
};

export default CloudArtwork;
